import type { Doctor, Visit, Period } from "@/types";
import { PRIO_TARGET, POT_BONUS } from "./constants";

export function visitsInPeriod(
  doctorId: string,
  visits: Record<string, Visit[]>,
  period: Period
): Visit[] {
  const result: Visit[] = [];
  for (const [date, dayVisits] of Object.entries(visits)) {
    if (date >= period.start && date <= period.end) {
      for (const v of dayVisits) {
        if (v.doctorId === doctorId) result.push(v);
      }
    }
  }
  return result;
}

export function calcScore(
  doctor: Doctor,
  visits: Record<string, Visit[]>,
  period: Period
): number {
  const target = PRIO_TARGET[doctor.prio];
  const periodVisits = visitsInPeriod(doctor.id, visits, period);
  const freq = Math.min(100, (periodVisits.length / target) * 100);

  let recency = 0;
  if (periodVisits.length > 0) {
    const lastDate = periodVisits
      .map((v) => v.date)
      .sort()
      .pop()!;
    const daysSince = Math.floor(
      (Date.now() - new Date(lastDate).getTime()) / 86400000
    );
    recency = Math.max(0, 100 - daysSince * 2);
  }

  const bonus = POT_BONUS[doctor.pot];
  return Math.min(100, Math.round(freq * 0.6 + recency * 0.4 + bonus));
}

export function calcCompliance(
  doctor: Doctor,
  visits: Record<string, Visit[]>,
  period: Period
): { status: "ok" | "partial" | "none"; actual: number; target: number } {
  const target = PRIO_TARGET[doctor.prio];
  const actual = visitsInPeriod(doctor.id, visits, period).length;

  if (actual === 0) return { status: "none", actual, target };
  if (actual >= target) return { status: "ok", actual, target };
  return { status: "partial", actual, target };
}

export function isAtRisk(
  doctor: Doctor,
  visits: Record<string, Visit[]>,
  period: Period
): boolean {
  const compliance = calcCompliance(doctor, visits, period);
  const score = calcScore(doctor, visits, period);
  return compliance.status !== "ok" && score < 40;
}

export function nextVisitDate(
  doctor: Doctor,
  visits: Record<string, Visit[]>,
  period: Period
): string | null {
  const target = PRIO_TARGET[doctor.prio];
  const periodVisits = visitsInPeriod(doctor.id, visits, period);

  const periodDays = Math.max(
    1,
    Math.floor(
      (new Date(period.end).getTime() - new Date(period.start).getTime()) /
        86400000
    )
  );
  const interval = Math.round(periodDays / target);

  if (periodVisits.length === 0) return null;

  const lastDate = periodVisits
    .map((v) => v.date)
    .sort()
    .pop()!;
  const next = new Date(lastDate);
  next.setDate(next.getDate() + interval);
  return next.toISOString().slice(0, 10);
}
